import React, { useCallback, useEffect, useState } from 'react';

import {
  InputGroup,
  InputLeftElement,
  InputRightElement,
} from '@chakra-ui/react';

import { Input } from '../input';
import { divisor } from './constants';
import {
  getLocale,
  getNumberSeparators,
  sanitizeFormattedNumber,
} from './helpers';
import { CurrencyInputProps } from './types';

export const CurrencyInput = ({
  currency = '£',
  value: propValue,
  locale: propLocale,
  onChange,
  suffix,
  suffixWidth,
  ...inputProps
}: CurrencyInputProps) => {
  const locale = getLocale(propLocale);
  const { decimalSeparator, thousandsSeparator } = getNumberSeparators(locale);
  const [value, setValue] = useState(
    propValue && !Number.isNaN(propValue) ? propValue / divisor : ''
  );
  const [displayValue, setDisplayValue] = useState('');

  useEffect(() => {
    if (value === '') {
      setDisplayValue('');
    } else if (!Number.isNaN(value)) {
      setDisplayValue(new Intl.NumberFormat(locale).format(Number(value)));
    }
  }, [value, locale]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value: inputValue, name } = event.target;
      const lastChar = inputValue.slice(-1);
      const hasDecimalSeparator = displayValue.includes(decimalSeparator);
      const hasMultipleDecimalSeparators =
        inputValue.split(decimalSeparator).length > 2;

      if (hasMultipleDecimalSeparators) {
        return;
      }

      if (lastChar === decimalSeparator && !hasDecimalSeparator) {
        setDisplayValue(
          (prevDisplayValue) => `${prevDisplayValue}${decimalSeparator}`
        );
        return;
      }

      if (lastChar === '0' && hasDecimalSeparator) {
        setDisplayValue(inputValue);
        return;
      }

      const sanitizedInputValue = sanitizeFormattedNumber(
        inputValue,
        decimalSeparator,
        thousandsSeparator
      );
      setValue(sanitizedInputValue);

      if (onChange) {
        const updatedValue = (sanitizedInputValue * divisor)?.toString();
        const updatedEvent = {
          ...event,
          target: {
            ...event.target,
            value: updatedValue,
            name: name,
          },
        };
        onChange(updatedEvent);
      }
    },
    [displayValue, onChange, decimalSeparator, thousandsSeparator]
  );

  return (
    <InputGroup>
      <InputLeftElement pointerEvents="none" color="grey.400" w="10">
        {currency}
      </InputLeftElement>
      <Input
        {...inputProps}
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        pl="8"
        pr={suffix ? suffixWidth : undefined}
      />
      {suffix && (
        <InputRightElement
          pointerEvents="none"
          color="grey.400"
          w={suffixWidth}
        >
          {suffix}
        </InputRightElement>
      )}
    </InputGroup>
  );
};
